import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FiMail, FiLoader, FiArrowRight } from "react-icons/fi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/seo";
import { useAuth, useUser } from "./_hooks/useAuth";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/portal";

  const { requestCode, isLoading, isAuthenticated } = useAuth();
  const user = useUser();

  const [email, setEmail] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const targetPath = user.type === "admin" ? "/admin" : redirect;
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, redirect]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      toast.error("Please enter your email address");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const result = await requestCode(trimmedEmail);

    if (result.success) {
      // Store email in session storage for verify page
      sessionStorage.setItem("auth_email", trimmedEmail);

      // Store dev code if available (development only)
      if (result.devCode) {
        sessionStorage.setItem("auth_dev_code", result.devCode);
      }

      toast.success("Check your email for a login code");
      navigate(`/login/verify?redirect=${encodeURIComponent(redirect)}`);
    } else {
      toast.error(result.error || "Failed to send login code");
    }
  };

  return (
    <>
      <SEO
        title="Login"
        description="Sign in to your Path Financial Solutions customer portal to view documents and manage your account."
        url="/login"
        noIndex
      />

      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 sm:p-10">
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <span className="text-xl font-bold tracking-tight text-primary">
                  PATH FINANCIAL
                </span>
              </Link>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl font-medium text-primary mb-2">
                Welcome back
              </h1>
              <p className="text-muted-foreground">
                Sign in to your customer portal
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-foreground"
                >
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="pl-10"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-pill"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    Sending code...
                  </>
                ) : (
                  <>
                    Send login code
                    <FiArrowRight className="ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Helper text */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              We'll email you a 6-digit code for a password-free sign in.
            </p>

            {/* Contact link */}
            <div className="text-center mt-8 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link
                  to="/contact"
                  className="text-primary font-medium hover:underline"
                >
                  Contact us
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
